import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from "react-redux";
import { selectUserDetails, selectUserRoles } from "../../redux/slices/userSlice";
import AuthButton from './AuthButton';
import Container from 'react-bootstrap/Container';
import Nav from 'react-bootstrap/Nav';
import Navbar from 'react-bootstrap/Navbar';
import Image from 'react-bootstrap/Image';
import NavDropdown from 'react-bootstrap/NavDropdown';
import WatertronicsLogo from '../../images/Watertronics Logo.svg';
import { Link } from 'react-router-dom';
import FetchStatus from './FetchStatus';
import SignalRStatus from './SignalRStatus';
import { usePermissions } from '../../hooks/usePermissions';
import { SALES_PORTAL_URL } from '../../config/portalLinks';
import { PRODUCTION_PORTAL_PATH } from '../../config/productionRoutes';

const NAV_ITEMS = [
    { path: '/', label: 'Home' },
    { path: '/apps', label: 'Apps Portal', permissionKey: 'home.apps-portal' },
    { path: PRODUCTION_PORTAL_PATH, label: 'Production Portal', permissionKey: 'home.production-portal' },
    { path: '/sales', label: 'Sales Portal', permissionKey: 'home.sales-portal' },
    { path: '/controls', label: 'Controls', permissionKey: 'home.controls' },
    { path: '/engineering', label: 'Engineering Tools', permissionKey: 'home.engineering-tools' },
    { path: '/admin', label: 'Admin Page', permissionKey: 'admin.container' },
];

const TopNav = () => {
    const userDetails = useSelector(selectUserDetails);
    const userRoles = useSelector(selectUserRoles);
    const { canView, isLoading: isPermissionsLoading } = usePermissions();
    const version = process.env.REACT_APP_VERSION;
    const [environment, setEnvironment] = useState('');

    useEffect(() => {
        // Determine environment based on URL
        const url = window.location.hostname;
        if (url.includes('uat')) {
            setEnvironment('UAT');
        } else if (url.includes('localhost') || url.includes('127.0.0.1')) {
            setEnvironment('Development');
        } else {
            setEnvironment('Production');
        }
    }, []);

    const filteredMenu = useMemo(
        () => NAV_ITEMS.filter((item) => !item.permissionKey || (!isPermissionsLoading && canView(item.permissionKey))),
        [canView, isPermissionsLoading]
    );

    // Environment banner styles
    const getBannerStyle = () => {
        let bgColor = '#28a745'; // Green for Production
        let textColor = 'white';
        
        if (environment === 'UAT') {
            bgColor = '#ffc107'; // Yellow for UAT
            textColor = 'black';
        } else if (environment === 'Development') {
            bgColor = '#dc3545'; // Red for Development
            textColor = 'white';
        }
        
        return {
            backgroundColor: bgColor,
            color: textColor,
            padding: '4px 0',
            textAlign: 'center',
            fontWeight: 'bold',
            width: '100%',
            position: 'relative',
            top: 0,
            zIndex: 1000
        };
    };

    return (
        <>
            {/* Environment Banner */}
            {environment && environment !== 'Production' && (
                <div style={getBannerStyle()}>
                    {environment} Environment
                </div>
            )}
            <div className='userInfoBar'>
                <Link to="/" className="top-nav-logo-link">
                    <Image src={WatertronicsLogo} className='Logo' alt="Watertronics home" />
                </Link>
                <div className='topTitle'></div>

                {userRoles.length > 0 ? (
                    <Navbar expand="lg" className="fixBg top-nav-navbar">
                        <Container className='userDrop top-nav-container'>
                            <Navbar.Toggle
                                aria-controls="basic-navbar-nav"
                                aria-label="Toggle navigation"
                                className="top-nav-toggle"
                            />
                            <Navbar.Collapse id="basic-navbar-nav">
                                <Nav className="me-auto">
                                    <NavDropdown className='userNameDisplay top-nav-user-dropdown' title={userDetails.name} id="basic-nav-dropdown">
                                        {filteredMenu.map(item => (
                                            <NavDropdown.Item
                                                key={`${item.label}-${item.path}`}
                                                as={item.external ? 'a' : Link}
                                                href={item.external ? item.path : undefined}
                                                to={item.external ? undefined : item.path}
                                                className='userDropItems'
                                            >
                                                {item.label}
                                            </NavDropdown.Item>
                                        ))}
                                        <NavDropdown.Item className='userDropItems'>
                                            {userRoles.length > 0 ? 'User Role: ' + userRoles.join(', ') : ''}
                                        </NavDropdown.Item>
                                        <NavDropdown.Item className='userDropItems'>
                                            {userDetails ? <AuthButton /> : ''}
                                        </NavDropdown.Item>
                                        <NavDropdown.Item className='versionDropItems'>
                                            Version: {version}
                                        </NavDropdown.Item>
                                    </NavDropdown>
                                    <div className="top-nav-flat-menu">
                                        <div className="top-nav-flat-user">
                                            {userDetails.name}
                                        </div>
                                        {filteredMenu.map(item => (
                                            <Nav.Link
                                                key={`compact-${item.label}-${item.path}`}
                                                as={item.external ? 'a' : Link}
                                                href={item.external ? item.path : undefined}
                                                to={item.external ? undefined : item.path}
                                                className='userDropItems top-nav-flat-link'
                                            >
                                                {item.label}
                                            </Nav.Link>
                                        ))}
                                        <div className='userDropItems top-nav-flat-meta'>
                                            {userRoles.length > 0 ? 'User Role: ' + userRoles.join(', ') : ''}
                                        </div>
                                        <div className='userDropItems top-nav-flat-auth'>
                                            {userDetails ? <AuthButton /> : ''}
                                        </div>
                                        <div className='versionDropItems top-nav-flat-version'>
                                            Version: {version}
                                        </div>
                                    </div>
                                </Nav>
                            </Navbar.Collapse>
                            <SignalRStatus />
                            <FetchStatus />
                        </Container>
                            
                    </Navbar>
                ) : (
                    <div className='topLogin'>
                        <AuthButton />
                    </div>
                )}
            </div>
        </>
    );
};

export default TopNav;
